'use client';

import Link from 'next/link';

import { useLocale } from '@/contexts/LocaleContext';
import { getDictionary } from '@/utils/getDictionary';
import { pushGTMEvent } from '@/utils/pushGTMEvent';

import styles from './Footer.module.scss';

interface ColumnSocialProps {
  socialMedia?: {
    platform?:
      | ('facebook' | 'instagram' | 'linkedin' | 'twitter' | 'youtube')
      | null;
    url?: string | null;
    id?: string | null;
  }[];
}

export function ColumnSocial({ socialMedia }: ColumnSocialProps) {
  const locale = useLocale();
  const t = getDictionary(locale);

  const handleClick = (meta: { social_name: string }) => () => {
    pushGTMEvent({
      event: 'social_click',
      ...meta,
    });
  };

  if (!socialMedia || socialMedia.length === 0) return null;

  return (
    <div className={styles.columnSocial}>
      {socialMedia?.map((item, index) => {
        if (!item.url) return null;

        return (
          <div key={index} className={styles.columnSocialItem}>
            <Link
              onClick={handleClick({
                social_name: item.platform || '',
              })}
              href={item.url}
              target='_blank'
              aria-label={`${t('goTo')} ${item.platform} ${t('profile')}`}
            >
              {item.platform === 'linkedin' && (
                <svg
                  width='32'
                  height='33'
                  viewBox='0 0 32 33'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M0 16.0273C0 7.19079 7.16344 0.0273438 16 0.0273438C24.8366 0.0273438 32 7.19079 32 16.0273C32 24.8639 24.8366 32.0273 16 32.0273C7.16344 32.0273 0 24.8639 0 16.0273Z'
                    fill='white'
                  />
                  <path
                    d='M11.6 24.0273H8.2V13.3273H11.6V24.0273ZM9.9 11.8273C8.8 11.8273 8 11.0273 8 9.92734C8 8.82734 8.9 8.02734 9.9 8.02734C11 8.02734 11.8 8.82734 11.8 9.92734C11.8 11.0273 11 11.8273 9.9 11.8273ZM24 24.0273H20.6V18.2273C20.6 16.5273 19.9 16.0273 18.9 16.0273C17.9 16.0273 16.9 16.8273 16.9 18.3273V24.0273H13.5V13.3273H16.7V14.8273C17 14.1273 18.2 13.0273 19.9 13.0273C21.8 13.0273 23.8 14.1273 23.8 17.4273V24.0273H24Z'
                    fill='#2A0D5D'
                  />
                </svg>
              )}
              {item.platform === 'facebook' && (
                <svg
                  width='32'
                  height='33'
                  viewBox='0 0 32 33'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M0 16.0273C0 7.19079 7.16344 0.0273438 16 0.0273438C24.8366 0.0273438 32 7.19079 32 16.0273C32 24.8639 24.8366 32.0273 16 32.0273C7.16344 32.0273 0 24.8639 0 16.0273Z'
                    fill='white'
                  />
                  <path
                    d='M24 16.0273C24 11.6273 20.4 8.02734 16 8.02734C11.6 8.02734 8 11.6273 8 16.0273C8 20.0273 10.9 23.3273 14.7 23.9273V18.3273H12.7V16.0273H14.7V14.2273C14.7 12.2273 15.9 11.1273 17.7 11.1273C18.6 11.1273 19.5 11.3273 19.5 11.3273V13.3273H18.5C17.5 13.3273 17.2 13.9273 17.2 14.5273V16.0273H19.4L19 18.3273H17.1V24.0273C21.1 23.4273 24 20.0273 24 16.0273Z'
                    fill='#2A0D5D'
                  />
                </svg>
              )}

              {item.platform === 'instagram' && (
                <svg
                  width='32'
                  height='33'
                  viewBox='0 0 32 33'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M0 16.0273C0 7.19079 7.16344 0.0273438 16 0.0273438C24.8366 0.0273438 32 7.19079 32 16.0273C32 24.8639 24.8366 32.0273 16 32.0273C7.16344 32.0273 0 24.8639 0 16.0273Z'
                    fill='white'
                  />
                  <path
                    d='M15.9992 9.22754C18.1992 9.22754 18.4992 9.22754 19.3992 9.22754C20.1992 9.22754 20.5992 9.42754 20.8992 9.52754C21.2992 9.72754 21.5992 9.82754 21.8992 10.1275C22.1992 10.4275 22.3992 10.7275 22.4992 11.1275C22.5992 11.4275 22.6992 11.8275 22.7992 12.6275C22.7992 13.5275 22.7992 13.7275 22.7992 16.0275C22.7992 18.3275 22.7992 18.5275 22.7992 19.4275C22.7992 20.2275 22.5992 20.6275 22.4992 20.9275C22.2992 21.3275 22.1992 21.6275 21.8992 21.9275C21.5992 22.2275 21.2992 22.4275 20.8992 22.5275C20.5992 22.6275 20.1992 22.7275 19.3992 22.8275C18.4992 22.8275 18.2992 22.8275 15.9992 22.8275C13.6992 22.8275 13.4992 22.8275 12.5992 22.8275C11.7992 22.8275 11.3992 22.6275 11.0992 22.5275C10.6992 22.3275 10.3992 22.2275 10.0992 21.9275C9.79922 21.6275 9.59922 21.3275 9.49922 20.9275C9.39922 20.6275 9.29922 20.2275 9.19922 19.4275C9.19922 18.5275 9.19922 18.3275 9.19922 16.0275C9.19922 13.7275 9.19922 13.5275 9.19922 12.6275C9.19922 11.8275 9.39922 11.4275 9.49922 11.1275C9.69922 10.7275 9.79922 10.4275 10.0992 10.1275C10.3992 9.82754 10.6992 9.62754 11.0992 9.52754C11.3992 9.42754 11.7992 9.32754 12.5992 9.22754C13.4992 9.22754 13.7992 9.22754 15.9992 9.22754ZM15.9992 7.72754C13.6992 7.72754 13.4992 7.72754 12.5992 7.72754C11.6992 7.72754 11.0992 7.92754 10.5992 8.12754C10.0992 8.32754 9.59922 8.62754 9.09922 9.12754C8.59922 9.62754 8.39922 10.0275 8.09922 10.6275C7.89922 11.1275 7.79922 11.7275 7.69922 12.6275C7.69922 13.5275 7.69922 13.8275 7.69922 16.0275C7.69922 18.3275 7.69922 18.5275 7.69922 19.4275C7.69922 20.3275 7.89922 20.9275 8.09922 21.4275C8.29922 21.9275 8.59922 22.4275 9.09922 22.9275C9.59922 23.4275 9.99922 23.6275 10.5992 23.9275C11.0992 24.1275 11.6992 24.2275 12.5992 24.3275C13.4992 24.3275 13.7992 24.3275 15.9992 24.3275C18.1992 24.3275 18.4992 24.3275 19.3992 24.3275C20.2992 24.3275 20.8992 24.1275 21.3992 23.9275C21.8992 23.7275 22.3992 23.4275 22.8992 22.9275C23.3992 22.4275 23.5992 22.0275 23.8992 21.4275C24.0992 20.9275 24.1992 20.3275 24.2992 19.4275C24.2992 18.5275 24.2992 18.2275 24.2992 16.0275C24.2992 13.8275 24.2992 13.5275 24.2992 12.6275C24.2992 11.7275 24.0992 11.1275 23.8992 10.6275C23.6992 10.1275 23.3992 9.62754 22.8992 9.12754C22.3992 8.62754 21.9992 8.42754 21.3992 8.12754C20.8992 7.92754 20.2992 7.82754 19.3992 7.72754C18.4992 7.72754 18.2992 7.72754 15.9992 7.72754Z'
                    fill='#2A0D5D'
                  />
                  <path
                    d='M15.9992 11.7275C13.5992 11.7275 11.6992 13.6275 11.6992 16.0275C11.6992 18.4275 13.5992 20.3275 15.9992 20.3275C18.3992 20.3275 20.2992 18.4275 20.2992 16.0275C20.2992 13.6275 18.3992 11.7275 15.9992 11.7275ZM15.9992 18.8275C14.4992 18.8275 13.1992 17.6275 13.1992 16.0275C13.1992 14.5275 14.3992 13.2275 15.9992 13.2275C17.4992 13.2275 18.7992 14.4275 18.7992 16.0275C18.7992 17.5275 17.4992 18.8275 15.9992 18.8275Z'
                    fill='#2A0D5D'
                  />
                  <path
                    d='M20.3992 12.6275C20.9515 12.6275 21.3992 12.1798 21.3992 11.6275C21.3992 11.0753 20.9515 10.6275 20.3992 10.6275C19.8469 10.6275 19.3992 11.0753 19.3992 11.6275C19.3992 12.1798 19.8469 12.6275 20.3992 12.6275Z'
                    fill='#2A0D5D'
                  />
                </svg>
              )}

              {item.platform === 'youtube' && (
                <svg
                  width='32'
                  height='33'
                  viewBox='0 0 32 33'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M0 16.0273C0 7.19079 7.16344 0.0273438 16 0.0273438C24.8366 0.0273438 32 7.19079 32 16.0273C32 24.8639 24.8366 32.0273 16 32.0273C7.16344 32.0273 0 24.8639 0 16.0273Z'
                    fill='white'
                  />
                  <path
                    d='M23.6 12.1272C23.4 11.4272 22.9 10.9272 22.2 10.7272C21 10.4272 15.9 10.4272 15.9 10.4272C15.9 10.4272 10.9 10.4272 9.60001 10.7272C8.90001 10.9272 8.4 11.4272 8.2 12.1272C8 13.4272 8 16.0272 8 16.0272C8 16.0272 8 18.6272 8.3 19.9272C8.5 20.6272 9 21.1272 9.7 21.3272C10.9 21.6272 16 21.6272 16 21.6272C16 21.6272 21 21.6272 22.3 21.3272C23 21.1272 23.5 20.6272 23.7 19.9272C24 18.6272 24 16.0272 24 16.0272C24 16.0272 24 13.4272 23.6 12.1272ZM14.4 18.4272V13.6272L18.6 16.0272L14.4 18.4272Z'
                    fill='#2A0D5D'
                  />
                </svg>
              )}
            </Link>
          </div>
        );
      })}
    </div>
  );
}
